'use client';

import { API_ROUTES, ApiError, useApiMutation } from '@shared/api-utils';
import { useSignOut } from '@shared/auth';
import { Button, FormField } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { CloseAccount } from '@shared/icons/system';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import { Checkbox } from '../../../../../../../form-controls/Checkbox/Checkbox';
import { Link } from '../../../../../../../Link/Link';
import { toast } from '../../../../../../../Toast/Toast';
import type { CloseAccountRequest, CloseAccountResponse } from '../../../types/close-account';

const HELP_SECTION_PATH = 'pages.close_my_account.confirmation_step.help_section';
const REASONS_PATH = 'pages.close_my_account.reason_step.reasons';

const ConfirmationStep = () => {
  const { trigger, getValues, watch } = useFormContext();
  const t = useTranslations('my_account');
  const hasBlockersSection = t.has('pages.close_my_account.confirmation_step.blockers_list');
  const hasHelpSection = t.has(HELP_SECTION_PATH);
  const helpRoutes = hasHelpSection && 'CLOSE_ACCOUNT' in ROUTES.MY_ACCOUNT ? ROUTES.MY_ACCOUNT.CLOSE_ACCOUNT : undefined;
  const signOut = useSignOut();
  const selectedReason = watch('reason') as string;
  const reasonLabel = selectedReason && t.has(`${REASONS_PATH}.${selectedReason}`) ? t(`${REASONS_PATH}.${selectedReason}`) : selectedReason;

  const { mutateAsync: closeAccount } = useApiMutation<CloseAccountResponse, CloseAccountRequest>(API_ROUTES.MY_ACCOUNT.CLOSE_ACCOUNT, 'POST', {
    requestOptions: {
      credentials: true,
    },
    mutationOptions: {
      networkMode: 'always',
      retry: false,
    },
  });

  const notifyCloseAccountError = () => {
    toast(t('pages.close_my_account.error_step.message'), { type: 'error' });
  };

  const handleComplete = async () => {
    const isValid = await trigger(['confirmClosure']);
    if (!isValid) return;

    if (!navigator.onLine) {
      notifyCloseAccountError();
      return;
    }

    let isCancelled = false;
    const onOffline = () => {
      isCancelled = true;
      notifyCloseAccountError();
    };
    window.addEventListener('offline', onOffline);

    try {
      const reason = getValues('reason') as string;
      const response = await closeAccount({ reason });

      if (isCancelled || !navigator.onLine) {
        if (!isCancelled) {
          notifyCloseAccountError();
        }
        return;
      }

      if (response?.result !== true) {
        notifyCloseAccountError();
        return;
      }

      await signOut({ redirect: true, callbackUrl: '/' });
    } catch (error) {
      if (isCancelled) return;

      const { redirectsToLoginOnCloseAccountUnauthorised } = getSiteConfig().MY_ACCOUNT_SETTINGS;

      if (redirectsToLoginOnCloseAccountUnauthorised && error instanceof ApiError && error.getStatus() === 401) {
        await signOut({ redirect: true, callbackUrl: getSiteConfig().ROUTES.LOGIN });
        return;
      }

      notifyCloseAccountError();
    } finally {
      window.removeEventListener('offline', onOffline);
    }
  };

  return (
    <div className="w-full flex flex-col flex-1 gap-8" data-testid="confirmation-step">
      <CloseAccount size="extra-extra-large" data-testid="close-account-icon" />
      <div className="text-left">
        <h4>{t('pages.close_my_account.confirmation_step.title')}</h4>
      </div>

      <p data-testid="close-account-confirmation-message">
        {t.rich('pages.close_my_account.confirmation_step.message', {
          email_link: chunks => (
            <Link href={`mailto:${chunks}`} className="font-normal" simplified data-testid="close-account-support-email">
              {chunks}
            </Link>
          ),
        })}
      </p>

      {hasBlockersSection && (
        <ul className="list-disc list-outside ml-6 space-y-2" data-testid="close-account-blockers-list">
          <li className="leading-relaxed" data-testid="close-account-blocker-remaining-balance">
            {t('pages.close_my_account.confirmation_step.blockers_list.remaining_balance')}
          </li>
          <li className="leading-relaxed" data-testid="close-account-blocker-lottery-auto-renewals">
            {t('pages.close_my_account.confirmation_step.blockers_list.lottery_auto_renewals')}
          </li>
          <li className="leading-relaxed" data-testid="close-account-blocker-open-bets">
            {t('pages.close_my_account.confirmation_step.blockers_list.open_bets')}
          </li>
        </ul>
      )}

      {helpRoutes && (
        <>
          <p data-testid="close-account-help-safer-gambling">
            {t.rich(`${HELP_SECTION_PATH}.safer_gambling`, {
              link: chunks => (
                <Link
                  href={helpRoutes.RESPONSIBLE_GAMBLING}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="close-account-safer-gambling-link"
                  data-track-name="Safer Gambling"
                >
                  {chunks}
                </Link>
              ),
            })}
          </p>
          <p data-testid="close-account-help-contact-us">
            {t.rich(`${HELP_SECTION_PATH}.contact_us`, {
              link: chunks => (
                <Link href={helpRoutes.CONTACT_US} target="_blank" rel="noopener noreferrer" data-testid="close-account-contact-us-link" data-track-name="Contact">
                  {chunks}
                </Link>
              ),
            })}
          </p>
        </>
      )}

      <FormField
        name="confirmClosure"
        render={({ field, fieldState }) => (
          <Checkbox
            id="confirm-closure"
            label={t('pages.close_my_account.confirmation_step.confirm')}
            checked={field.value || false}
            onCheckedChange={field.onChange}
            helpMessage={fieldState.error?.message}
            helpMessageType={fieldState.error ? 'error' : undefined}
            trackingProps={{
              'data-track-name': 'Confirm',
              'data-track-selected': field.value ? 'Yes' : 'No',
            }}
            data-track-helper="Confirm"
          />
        )}
      />

      <div className="mt-auto" data-track-container="Actions">
        <Button data-testid="complete-integration" className="w-full" onClick={handleComplete} data-track-name="Close Account" data-track-selected={reasonLabel}>
          {t('actions_required.close_account')}
        </Button>
      </div>
    </div>
  );
};

export { ConfirmationStep };
